import React, { useState, useEffect, useRef } from "react";
import api from "../utils/api";
import WishlistButton from "./WishlistButton";
import LoginModal from "./LoginModal";

const HeroSection = () => {
  const [featuredGame, setFeaturedGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [videoExpanded, setVideoExpanded] = useState(false);
  const [videoVisible, setVideoVisible] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    const fetchFeaturedGame = async () => {
      try {
        console.log('🔍 Fetching featured game...');
        const response = await api.get('/featured-game');
        console.log('📥 Featured game response:', response.data);
        
        if (response.data) {
          const trailerUrl = response.data.trailerUrl
            ? response.data.trailerUrl.replace("watch?v=", "embed/")
            : null;
            
          console.log('🎥 Processed trailer URL:', trailerUrl);
            
          setFeaturedGame({
            ...response.data,
            trailerUrl
          });
          
          // Auto-expand video if trailer URL exists with a delay for animation
          if (trailerUrl) {
            // First, render the video section with h-0
            setVideoExpanded(true);
            
            // Then wait 1 second before starting the height animation
            setTimeout(() => {
              setVideoVisible(true);
              
              // Start scroll animation to follow the video
              const startTime = Date.now();
              const animationDuration = 3000; // 3 seconds
              const heroHeight = 600; // Hero section height
              const videoHeight = 600; // Video section height
              const totalScrollDistance = videoHeight;
              
              const scrollAnimation = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / animationDuration, 1);
                
                // Ease-in-out function for smooth scrolling
                const easeProgress = progress < 0.5 
                  ? 2 * progress * progress 
                  : 1 - Math.pow(-2 * progress + 2, 2) / 2;
                
                const currentScroll = easeProgress * totalScrollDistance;
                
                // Scroll to keep the video in view
                window.scrollTo({
                  top: currentScroll,
                  behavior: 'auto' // Use 'auto' for smooth animation
                });
                
                if (progress < 1) {
                  requestAnimationFrame(scrollAnimation);
                }
              };
              
              // Start the scroll animation
              requestAnimationFrame(scrollAnimation);
            }, 1000);
          }
        }
      } catch (error) {
        console.error('❌ Error fetching featured game:', error);
        setError('Failed to load featured game');
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedGame();
  }, []);

  const togglePlay = () => {
    if (iframeRef.current) {
      const iframe = iframeRef.current;
      const message = isPlaying ? 'pauseVideo' : 'playVideo';
      iframe.contentWindow.postMessage(`{"event":"command","func":"${message}","args":""}`, '*');
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (iframeRef.current) {
      const iframe = iframeRef.current;
      const message = isMuted ? 'unMute' : 'mute';
      iframe.contentWindow.postMessage(`{"event":"command","func":"${message}","args":""}`, '*');
      setIsMuted(!isMuted);
    }
  };

  return (
    <>
      {/* Main Hero Section - Always Visible */}
      <section className="relative h-[600px] bg-white">
        <div className="max-w-7xl mx-auto h-full flex flex-col md:flex-row items-center justify-between gap-10 px-6 py-8">
          {/* Left Text Content */}
          <div className="flex-1 text-center md:text-left">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 leading-tight mb-4">
              Gaming at Your Fingertips
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 mb-6">
              Track upcoming releases, discover new favorites, and never miss a game launch again.
            </p>
            <button 
              onClick={() => setShowLoginModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-2xl transition duration-300 shadow-lg"
            >
              Sign Up
            </button>
          </div>

          {/* Right Illustration */}
          <div className="flex-1 hidden md:block">
            <img
              src="/HeroSectionLogo.png"
              alt="Gaming console illustration"
              className="w-full max-w-md mx-auto"
            />
          </div>
        </div>
      </section>

      {/* Featured Video Section - Slides Down Automatically */}
      {featuredGame?.trailerUrl && videoExpanded && (
        <div 
          className={`relative overflow-hidden transition-all duration-[3000ms] ease-in-out ${
            videoVisible ? 'h-[600px] opacity-100' : 'h-0 opacity-0'
          }`}
        >
          {/* Video Background */}
          <div className="absolute inset-0 w-full h-full pointer-events-none">
            <iframe
              ref={iframeRef}
              src={`${featuredGame.trailerUrl}?autoplay=1&mute=1&controls=0&showinfo=0&rel=0&modestbranding=1&playlist=${featuredGame.trailerUrl.split('/').pop()}&enablejsapi=1&playsinline=1`}
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[100vw] h-[100vh] min-w-[100vw] min-h-[100vh] object-cover"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Featured Game Trailer"
              frameBorder="0"
            />
          </div>

          {/* Overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-40" />

          {/* Game Title - Top Left */}
          <div className="absolute top-8 left-8 z-20">
            <h2 className="text-4xl sm:text-5xl font-extrabold text-white leading-tight drop-shadow-lg">
              {featuredGame.name}
            </h2>
          </div>

          {/* Wishlist Button - Bottom Left */}
          <div className="absolute bottom-8 left-8 z-20">
            <WishlistButton 
              gameId={featuredGame.id}
              releaseDate={featuredGame.releaseDateUnix}
            />
          </div>

          {/* Video Controls - Bottom Right */}
          <div className="absolute bottom-8 right-8 z-20 flex gap-4">
            <button
              onClick={togglePlay}
              className="bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition duration-300 shadow-lg"
              title={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </button>
            <button
              onClick={toggleMute}
              className="bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition duration-300 shadow-lg"
              title={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      )}
      
      {/* Login Modal */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)}
      />
    </>
  );
};

export default HeroSection;
